import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { fetchProfilePrefs, persistThemePreference } from "@/lib/profile-prefs";

export type Theme = "system" | "light" | "dark";
export type ResolvedTheme = "light" | "dark";

type ThemeCtx = {
  theme: Theme;
  resolvedTheme: ResolvedTheme;
  setTheme: (t: Theme) => void;
};

const STORAGE_KEY = "aria:theme";
const Ctx = createContext<ThemeCtx>({ theme: "system", resolvedTheme: "dark", setTheme: () => {} });

function getSystem(): ResolvedTheme {
  if (typeof window === "undefined") return "dark";
  return window.matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark";
}

function readStored(): Theme {
  if (typeof window === "undefined") return "dark";
  const v = window.localStorage.getItem(STORAGE_KEY);
  return v === "light" || v === "dark" || v === "system" ? v : "dark";
}

function apply(resolved: ResolvedTheme) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  root.classList.remove("light", "dark");
  root.classList.add(resolved);
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute("content", resolved === "light" ? "#f4f2ed" : "#090a0c");
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => readStored());
  const [resolvedTheme, setResolved] = useState<ResolvedTheme>(() =>
    theme === "system" ? getSystem() : theme,
  );

  useEffect(() => {
    const next = theme === "system" ? getSystem() : theme;
    setResolved(next);
    apply(next);
  }, [theme]);

  useEffect(() => {
    if (theme !== "system") return;
    const mq = window.matchMedia("(prefers-color-scheme: light)");
    const onChange = () => {
      const next: ResolvedTheme = mq.matches ? "light" : "dark";
      setResolved(next);
      apply(next);
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [theme]);

  const setTheme = (t: Theme) => {
    try { window.localStorage.setItem(STORAGE_KEY, t); } catch {}
    setThemeState(t);
    void persistThemePreference(t).catch(() => {});
  };

  // Account is the source of truth once the profile loads: signing in on a new
  // browser restores the saved theme instead of falling back to the default.
  useEffect(() => {
    let active = true;
    const sync = () => {
      void fetchProfilePrefs()
        .then((prefs) => {
          if (!active || !prefs) return;
          setThemeState((prev) => {
            if (prev === prefs.theme) return prev;
            try { window.localStorage.setItem(STORAGE_KEY, prefs.theme); } catch {}
            return prefs.theme;
          });
        })
        .catch(() => {});
    };
    sync();
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_IN" || event === "USER_UPDATED") sync();
    });
    return () => {
      active = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  return <Ctx.Provider value={{ theme, resolvedTheme, setTheme }}>{children}</Ctx.Provider>;
}

export function useTheme() {
  return useContext(Ctx);
}